import { useHousehold } from "../../app/useHousehold";
import { useF1 } from "../../data/newsQueries";
import { dataStateOf } from "../../data/queries";
import { EmptyState } from "../../ui/StatusBadge";
import { formatShortTime, useNow } from "../../ui/time";
import { remPx, SkyFoot } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { NewsImage } from "./newsUi";

type F1Config = Record<string, never>;

/** Team colors, by Jolpica constructor id. */
const TEAM_COLOR: Record<string, string> = {
  mercedes: "#27f4d2",
  ferrari: "#e8002d",
  red_bull: "#3671c6",
  mclaren: "#ff8000",
  aston_martin: "#229971",
  alpine: "#0093cc",
  williams: "#64c4ff",
  rb: "#6692ff",
  sauber: "#52e252",
  audi: "#bb0a30",
  haas: "#b6babd",
  cadillac: "#d4af37",
};

export function untilText(iso: string, now: Date): string {
  const ms = Date.parse(iso) - now.getTime();
  if (ms <= 0) return ms > -3 * 3_600_000 ? "Racing now" : "Done";
  const h = Math.round(ms / 3_600_000);
  if (h < 1) return "within the hour";
  if (h < 36) return `in ${h} h`;
  return `in ${Math.round(h / 24)} days`;
}

/** Formula 1: the next race (with its track), the drivers' and constructors' standings, the last podium. */
function F1({ size }: WidgetProps<F1Config>) {
  const { settings, timeZone, isAdmin } = useHousehold();
  const now = useNow("minute");
  const on = settings.news.f1;
  const q = useF1(on);
  const state = dataStateOf(q, 6 * 60 * 60_000);
  if (!on)
    return (
      <EmptyState
        icon="trophy"
        title="Formula 1 is off"
        detail="Turn it on in Admin → News & sports."
        action={
          isAdmin ? (
            <a className="btn btn-sm btn-primary" href="/admin#news">
              News & sports
            </a>
          ) : undefined
        }
      />
    );
  const d = q.data;
  if (!d) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="trophy" title="F1 unavailable" detail={q.error?.message} />;
  const rem = remPx();
  const showTeams = size.height > rem * 26;
  // Header, next race, last podium and footer take about 15rem; the constructors line another 2.5.
  const rows = Math.max(3, Math.min(10, Math.floor((size.height - rem * (showTeams ? 17.5 : 15)) / (rem * 1.85))));
  const when = (iso: string) => {
    const date = new Date(iso);
    const day = new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short", month: "short", day: "numeric" }).format(date);
    return `${day} · ${formatShortTime(date, timeZone, settings.clock24h)}`;
  };
  const leader = d.drivers[0]?.points ?? 0;

  return (
    <div className="news-sport f1">
      <WidgetHeader title="Formula 1" meta={d.round ? `${d.season} · after round ${d.round}` : d.season} />
      {d.next && (
        <div className="f1-next">
          <NewsImage src={d.next.image} className="f1-track" alt={`${d.next.circuit} layout`} />
          <span className="f1-next-text">
            <span className="news-eyebrow">Next{d.next.round && ` · Round ${d.next.round}`}</span>
            <strong>{d.next.name}</strong>
            <span className="muted small">
              {d.next.locality}, {d.next.country}
            </span>
            <span>
              {when(d.next.start)} <span className="f1-until">{untilText(d.next.start, now)}</span>
            </span>
            {d.next.qualifying && <span className="muted small">Qualifying {when(d.next.qualifying)}</span>}
          </span>
        </div>
      )}
      <ol className="f1-table">
        {d.drivers.slice(0, rows).map((x) => (
          <li key={x.code || x.name} style={{ ["--team" as string]: x.color ?? TEAM_COLOR[x.teamId] ?? "var(--text-2)" }}>
            <span className="f1-pos">{x.position}</span>
            <i className="f1-bar" aria-hidden="true" />
            <span className="f1-driver">
              {!x.team && <NewsImage src={x.flag} className="f1-flag" alt="" />}
              <b>{x.name}</b>
              {x.team && <small>{x.team}</small>}
            </span>
            <span className="f1-pts">{x.points}</span>
            <small className="f1-gap">{x.position === 1 ? (x.wins !== null ? `${x.wins} wins` : "leads") : `−${leader - x.points}`}</small>
          </li>
        ))}
      </ol>
      {showTeams && d.constructors.length > 0 && (
        <div className="f1-teams">
          {d.constructors.slice(0, 5).map((t) => (
            <span key={t.id} className="f1-team" style={{ ["--team" as string]: t.color ?? TEAM_COLOR[t.id] ?? "var(--text-2)" }}>
              {t.position}. {t.name} <b>{t.points}</b>
            </span>
          ))}
        </div>
      )}
      {d.last && (
        <p className="f1-last">
          <span className="news-eyebrow">{d.last.name}</span> {d.last.podium.map((p, n) => `${["🥇", "🥈", "🥉"][n]} ${p.name}`).join("  ")}
        </p>
      )}
      <SkyFoot source="ESPN · Jolpica F1 · Wikipedia" state={state} updatedAt={q.dataUpdatedAt} />
    </div>
  );
}

export const f1Widget: WidgetDefinition<F1Config> = {
  type: "f1",
  version: 1,
  name: "Formula 1",
  description: "The next Grand Prix with its track, the championship standings and the last podium.",
  icon: "trophy",
  category: "household",
  defaultSize: { w: 4, h: 6 },
  minSize: { w: 3, h: 4 },
  stackHeight: 560,
  defaults: {},
  settings: [],
  provider: "jolpica",
  Component: F1,
};
